// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { type State } from 'store';
import SearchResults from 'components/search/SearchResults';
import type { PostItem } from 'store/modules/listing';
import { SearchActions } from 'store/actionCreators';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';

type Props = {
  result: ?{
    count: number,
    data: PostItem[],
  },
  currentKeyword: string,
  currentPage: number,
  currentMenu: string,
  currentFacets: string,
  currentSort: string,
  menu: string,
  pending: boolean,
};

class SearchResultsContainer extends Component<Props> {
  constructor(props) {
    super(props);

    // URL에서 초기 상태 설정
    const { facets, sort } = queryString.parse(props.location.search);
    const initialFacets = facets ? JSON.parse(facets) : {};
    const initialSort = sort || 'views:desc';

    this.state = {
      selectedFacets: initialFacets,
      currentSort: initialSort,
    };
  }

  componentDidMount() {
    const { q, facets, sort } = queryString.parse(this.props.location.search);
    const urlKeyword = q || '';
    const urlFacets = facets || '{}';
    const urlSort = sort || 'views:desc';

    // 스토어의 검색 조건과 URL이 완전히 동일하고 데이터가 있으면 API 호출 스킵 (뒤로가기)
    if (
      this.props.currentKeyword === urlKeyword &&
      this.props.currentFacets === urlFacets &&
      this.props.currentSort === urlSort &&
      this.props.result &&
      this.props.result.hits &&
      this.props.result.hits.length > 0
    ) {
      return;
    }

    // 초기 마운트 시 검색 실행
    this.performDirectSearch();
  }

  onSearchNext = () => {
    const { currentKeyword, currentPage, currentMenu, pending } = this.props;
    if (pending) return;

    // URL 업데이트 없이 검색만 실행 (중복 호출 방지)
    SearchActions.nextPublicSearch({
      q: currentKeyword || '',
      page: currentPage + 1,
      menu: currentMenu,
      facets: JSON.stringify(this.state.selectedFacets),
      sort: this.state.currentSort,
    });
  };

  onFacetChange = (facet: string, value: string) => {
    this.setState(
      prevState => {
        const newFacets = { ...prevState.selectedFacets };
        if (value === '' || value === null) {
          delete newFacets[facet];
        } else {
          newFacets[facet] = value;
        }
        return { selectedFacets: newFacets };
      },
      () => {
        // URL 업데이트와 함께 검색 실행
        this.updateURLAndSearch();
      },
    );
  };

  onSortChange = (sortBy: string) => {
    this.setState({ currentSort: sortBy }, () => {
      // URL 업데이트와 함께 검색 실행
      this.updateURLAndSearch();
    });
  };

  onKeywordChange = (newKeyword: string) => {
    const { menu, history } = this.props;

    // URL 쿼리 파라미터 구성
    const queryParams = new URLSearchParams();
    queryParams.set('q', newKeyword);
    queryParams.set('menu', menu || 'products');

    if (this.state.selectedFacets && Object.keys(this.state.selectedFacets).length > 0) {
      queryParams.set('facets', JSON.stringify(this.state.selectedFacets));
    }

    if (this.state.currentSort && this.state.currentSort !== 'views:desc') {
      queryParams.set('sort', this.state.currentSort);
    }

    // URL 업데이트 (replace 사용)
    history.replace(`/search?${queryParams.toString()}`);

    // 검색 실행
    SearchActions.publicSearch({
      q: newKeyword,
      page: 1,
      menu,
      facets: JSON.stringify(this.state.selectedFacets),
      sort: this.state.currentSort,
    });
  };

  updateURLAndSearch = () => {
    const { menu, history } = this.props;
    const { q } = queryString.parse(this.props.location.search);
    const searchQuery = q !== undefined ? q : '';

    // URL 쿼리 파라미터 구성
    const queryParams = new URLSearchParams();
    queryParams.set('q', searchQuery);
    queryParams.set('menu', menu || 'products');

    if (this.state.selectedFacets && Object.keys(this.state.selectedFacets).length > 0) {
      queryParams.set('facets', JSON.stringify(this.state.selectedFacets));
    }

    if (this.state.currentSort && this.state.currentSort !== 'views:desc') {
      queryParams.set('sort', this.state.currentSort);
    }

    // URL 업데이트 (replace 사용하여 히스토리 스택에 쌓이지 않도록)
    history.replace(`/search?${queryParams.toString()}`);

    // 검색 실행
    SearchActions.publicSearch({
      q: searchQuery,
      page: 1,
      menu,
      facets: JSON.stringify(this.state.selectedFacets),
      sort: this.state.currentSort,
    });
  };

  performDirectSearch = () => {
    const { menu } = this.props;
    const { q } = queryString.parse(this.props.location.search);

    SearchActions.publicSearch({
      q: q !== undefined ? q : '',
      page: 1,
      menu,
      facets: JSON.stringify(this.state.selectedFacets),
      sort: this.state.currentSort,
    });
  };

  componentDidUpdate(prevProps) {
    // URL 변경이 없으면 아무것도 하지 않음
    if (this.props.location.search === prevProps.location.search) {
      return;
    }

    const { menu } = this.props;
    const currentQuery = queryString.parse(this.props.location.search);
    const { q, facets, sort } = currentQuery;

    // 상태를 URL과 동기화
    const newFacets = facets ? JSON.parse(facets) : {};
    const newSort = sort || 'views:desc';

    this.setState({
      selectedFacets: newFacets,
      currentSort: newSort,
    });

    // URL이 변경되면 항상 API 호출
    SearchActions.publicSearch({
      q: q || '',
      page: 1,
      menu,
      facets: facets || '{}',
      sort: newSort,
    });
  }

  componentWillUnmount() {
    // 검색 결과는 유지하고 스크롤 위치만 저장
    // SearchActions.initialize(); // 제거: 검색 결과 유지
  }

  render() {
    return (
      <SearchResults
        result={this.props.result}
        onSearchNext={this.onSearchNext}
        menu={this.props.menu}
        currentKeyword={this.props.currentKeyword}
        onFacetChange={this.onFacetChange}
        selectedFacets={this.state.selectedFacets}
        onSortChange={this.onSortChange}
        currentSort={this.state.currentSort}
        currentUser={this.props.currentUser}
        onKeywordChange={this.onKeywordChange}
      />
    );
  }
}

export default connect(
  (state: State) => ({
    result: state.search.result,
    currentKeyword: state.search.currentKeyword,
    currentPage: state.search.currentPage,
    currentMenu: state.search.currentMenu,
    currentFacets: state.search.currentFacets,
    currentSort: state.search.currentSort,
    currentUser: state.user.user,
    pending:
      state.pender.pending['search/NEXT_PUBLIC_SEARCH'] ||
      state.pender.pending['search/PUBLIC_SEARCH'],
  }),
  () => ({}),
)(withRouter(SearchResultsContainer));



// WEBPACK FOOTER //
// ./src/containers/search/SearchResultsContainer.js